import React from 'react';
import { useDropzone } from 'react-dropzone';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ErrorText, P, SmallInstructions } from '@styled';
import LoadingArea from '../LoadingArea';

import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { useScreenSmDown } from '@hooks/useResponsive';

const Container = styled.div`
    width: 100%;
    height: 80px;
    border-color: #e4eaee;
    border-width: 2px;
    border-style: dashed;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    @media ${mediaQueries.md} {
        height: 300px;
    }
`;
const DragDropContainer = styled.div`
    width: 100%;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    cursor: pointer;
    background-color: ${props => (props.isDragActive ? colors.lightGray : 'transparent')};
    @media ${mediaQueries.md} {
        height: 300px;
    }
`;

const getErrorMessage = ({ code, acceptedFormatDisplay, maxSizeMB }) => {
    if (code === 'file-too-large') {
        return `ERROR: The file is too big. Max file size: ${maxSizeMB}MB`;
    }
    if (code === 'file-type') {
        return `ERROR: This type of file is not accepted Please upload: ${acceptedFormatDisplay.join(', ')}`;
    }
    return 'There has been an error uploading this file. Please try a different file.';
};

const DragDropFile = ({
    onDrop,
    acceptedFormatDisplay = ['JPG'],
    acceptedFormats = 'image/jpeg',
    maxSizeMB = 5,
    isLoading = true,
    minPixels = 200,
}) => {
    const isMobile = useScreenSmDown();
    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        maxFiles: 1,
        // in bytes
        maxSize: maxSizeMB * 1000000,
        accept: acceptedFormats,
    });

    const renderErrors = () =>
        fileRejections.map(({ file, errors }) => (
            <li key={file.path}>
                <ul className="list-unstyled">
                    {errors.map(e => (
                        <li key={e.code}>
                            <ErrorText style={{ fontSize: '.9rem' }}>
                                {getErrorMessage({ code: e.code, acceptedFormatDisplay, maxSizeMB })}
                            </ErrorText>
                        </li>
                    ))}
                </ul>
            </li>
        ));

    return (
        <>
            {isLoading && (
                <Container>
                    <LoadingArea minHeight={296} minHeightMobile={76} />
                </Container>
            )}
            {!isLoading && (
                <Container>
                    <DragDropContainer {...getRootProps()} isDragActive={isDragActive}>
                        <input {...getInputProps()} />
                        <>
                            <FontAwesomeIcon icon={faCloudUploadAlt} size="lg" style={{ marginBottom: '.5rem' }} />
                            {isMobile && <P style={{ marginBottom: 0 }}>Choose a file</P>}
                            {!isMobile && (
                                <P>
                                    Drop files to upload or <span className="text-color-link">browse</span>
                                </P>
                            )}
                        </>
                    </DragDropContainer>
                </Container>
            )}
            <SmallInstructions style={{ marginTop: '.8rem' }}>
                Image should be {minPixels}px or larger
            </SmallInstructions>
            <SmallInstructions>Accepted image formats: {acceptedFormatDisplay.join(', ')}</SmallInstructions>
            <SmallInstructions>Maximum file size: {maxSizeMB}MB</SmallInstructions>
            {fileRejections.length > 0 && <ul className="list-unstyled">{renderErrors(fileRejections)}</ul>}
        </>
    );
};

DragDropFile.propTypes = {
    onDrop: PropTypes.func,
    acceptedFormatDisplay: PropTypes.array,
    acceptedFormats: PropTypes.string,
    maxSizeMB: PropTypes.number,
    minPixels: PropTypes.number,
    isLoading: PropTypes.bool,
};

export default DragDropFile;
